import { ValuesType } from 'react-uforms';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';

import axios from '../../service/axios';
import { isValue, nextRedirect } from '../../service/helpers';
import { ImageEntity } from '../../types';
import { disableLoading, enableLoading } from '../app/actions';
import { handleApiError } from '../app/thunks';
import { pushNotificationApiError } from '../notification/thunks';
import { RootState } from '../root-reducer';
import {
  cleanFooterMessage,
  clearAnswers,
  gotoPreviousStep,
  loadQuizImagesSucceeded,
  loadStepFailure,
  loadStepRequested,
  loadStepSucceeded,
  saveAnswers,
  setFooterMessage,
} from './actions';
import { currentQuestionsSelector } from './selectors';
import { NewStep, QuizValues } from './types';

export const showFooterMessage =
  (values?: ValuesType): ThunkAction<void, RootState, null, Action> =>
  async (dispatch, getState) => {
    const state = getState();
    if (!values) {
      values = state.quizState.answers;
    }
    const [question] = currentQuestionsSelector(state);
    if (question && question.footerMessage && isValue(values, question.name)) {
      const activeValue = values[question.name];
      if (Array.isArray(activeValue)) {
        const optionIds = activeValue;
        const labels = question.options
          .filter((option) => optionIds.includes(option.id))
          .map((option) => option.footerLabel || option.label);
        dispatch(setFooterMessage(question.footerMessage, labels));
      } else {
        dispatch(setFooterMessage(question.footerMessage, [activeValue]));
      }
    } else {
      dispatch(cleanFooterMessage());
    }
  };

export const loadStep =
  (slug = '', di = { axios, showFooterMessage, handleApiError }): ThunkAction<Promise<void>, RootState, null, Action> =>
  async (dispatch, getState) => {
    try {
      dispatch(loadStepRequested());
      slug = slug ? `${slug}/` : '';
      const asyncResp = await di.axios.post<NewStep>(`/quizzes/default/${slug}`, getState().quizState.answers);
      dispatch(loadStepSucceeded(asyncResp.data));
      dispatch(di.showFooterMessage());
    } catch (err) {
      dispatch(di.handleApiError(err));
      dispatch(loadStepFailure(err));
    }
  };

export const loadQuizImages = (): ThunkAction<void, RootState, null, Action> => async (dispatch) => {
  try {
    const asyncResp = await axios.get<{ stepImages: ImageEntity[]; optionImages: ImageEntity[] }>(
      '/quizzes/default/images/',
    );
    dispatch(loadQuizImagesSucceeded(asyncResp.data));
  } catch (err) {
    dispatch(handleApiError(err));
  }
};

export const goBack =
  (di = { nextRedirect }): ThunkAction<void, RootState, null, Action> =>
  async (dispatch, getState) => {
    const slug = getState().quizState.prevStepSlug;
    dispatch(gotoPreviousStep(slug));
    const query = slug !== '/' ? { query: { step: slug } } : undefined;
    di.nextRedirect(null, '/', null, query);
  };

export const goToLast =
  (di = { nextRedirect }): ThunkAction<void, RootState, null, Action> =>
  async (dispatch, getState) => {
    const slug = getState().quizState.lastStepSlug;
    di.nextRedirect(null, '/', null, { query: { step: slug } });
  };

export const startOver =
  (di = { nextRedirect }): ThunkAction<void, RootState, null, Action> =>
  async (dispatch) => {
    dispatch(clearAnswers());
    di.nextRedirect(null, '/');
  };

export const saveResults =
  (di = { nextRedirect, axios }): ThunkAction<Promise<void>, RootState, null, Action> =>
  async (dispatch, getState) => {
    try {
      dispatch(enableLoading());
      const { currentStep, answers } = getState().quizState;
      const { recaptchaToken } = getState().app;

      const { data } = await di.axios.post<{ hash: string }>('/results/', {
        quizId: currentStep.quizId,
        answers,
        recaptchaToken,
      });
      await di.nextRedirect(null, '/complete/[hash]', `/complete/${data.hash}`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      dispatch(handleApiError(err));
      dispatch(pushNotificationApiError(err));
    } finally {
      dispatch(disableLoading());
    }
  };

export const loadAnswers =
  (slug, di = { axios, handleApiError }): ThunkAction<Promise<void>, RootState, null, Action> =>
  async (dispatch) => {
    try {
      const asyncResp = await di.axios.get<QuizValues>(`/results/${slug}/`);
      dispatch(saveAnswers(asyncResp.data));
    } catch (err) {
      dispatch(di.handleApiError(err));
    }
  };
